import React, { useState } from 'react';
import type { KonvaEventObject } from 'konva/lib/Node';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps } from '../../core/tools/interfaces/ITool';
import { ICommand } from '../../core/commands/ICommand';
import { CreateFreehandPathCommand } from '../../core/commands/CreateFreehandPathCommand';
import { DeleteNodeCommand } from '../../core/commands/DeleteNodeCommand';
import type { Node as SceneNode } from '../../core/models/scene';
import { useDiagramStore } from '../../stores/diagramStore';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Button } from '../../components/ui/button';
import { Pen, Highlighter, Eraser } from 'lucide-react';
import { cn } from '../../lib/utils';

declare global {
  interface Window {
    __openPenTool?: () => void;
    __getPenColor?: () => string;
    __getPenTool?: () => string;
  }
}

const penTools = [
  {
    id: 'pen',
    label: 'Pen',
    icon: Pen,
    width: 2,
    opacity: 1
  },
  // {
  //   id: 'marker',
  //   label: 'Marker',
  //   icon: PenIcon,
  //   width: 4,
  //   opacity: 0.8
  // },
  {
    id: 'highlighter',
    label: 'Highlighter',
    icon: Highlighter,
    width: 12,
    opacity: 0.4
  },
  {
    id: 'eraser',
    label: 'Eraser',
    icon: Eraser,
    width: 20,
    opacity: 1
  },
];

const penColors = [
  '#000000', // Black
  '#EF4444', // Red
  '#3B82F6', // Blue
  '#16A34A', // Green
];

type PenEvent = KonvaEventObject<MouseEvent | TouchEvent>;
type PenMode = 'pen' | 'marker' | 'highlighter' | 'eraser';

interface PenExecuteParams {
  command?: ICommand;
  color?: string;
  width?: number;
  opacity?: number;
  tool?: PenMode;
}

const PenToolComponent: React.FC<IToolComponentProps> = ({ onExecute }) => {
  const [open, setOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState('pen');
  const [selectedColor, setSelectedColor] = useState('#000000');

  React.useEffect(() => {
    window.__openPenTool = () => setOpen(true);
    window.__getPenColor = () => selectedColor;
    window.__getPenTool = () => selectedTool;
    return () => {
      delete window.__openPenTool;
      delete window.__getPenColor;
      delete window.__getPenTool;
    };
  }, [selectedColor, selectedTool]);

  const handleToolSelect = (toolId: string) => {
    setSelectedTool(toolId);
    const tool = penTools.find(t => t.id === toolId);
    onExecute({
      tool: toolId,
      color: selectedColor,
      width: tool?.width || 2,
      opacity: tool?.opacity || 1
    });
  };

  const handleColorSelect = (color: string) => {
    setSelectedColor(color);
    const tool = penTools.find(t => t.id === selectedTool);
    onExecute({
      tool: selectedTool,
      color,
      width: tool?.width || 2,
      opacity: tool?.opacity || 1
    });
  };

  const CurrentIcon = penTools.find(t => t.id === selectedTool)?.icon || Pen;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="w-0 h-0 opacity-0 pointer-events-none fixed bottom-6 left-[calc(50%-40px)]"
        >
          <CurrentIcon className="h-5 w-5" />
          <div
            className="absolute bottom-1 right-1 w-2 h-2 rounded-full"
            style={{ backgroundColor: selectedColor }}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[180px] p-3"
        align="center"
        side="top"
        sideOffset={80}
      >
        <div className="space-y-3">
          {/* Tool Selection */}
          <div className="grid grid-cols-3 gap-1.5">
            {penTools.map((tool) => {
              const Icon = tool.icon;
              return (
                <button
                  key={tool.id}
                  type="button"
                  data-testid={`tid.tools.pen.mode.${tool.id}`}
                  onClick={() => handleToolSelect(tool.id)}
                  className={cn(
                    "w-full h-9 flex items-center justify-center",
                    "rounded-md border transition-all",
                    selectedTool === tool.id
                      ? "bg-[#F3F4F6] border-[#3478DD]"
                      : "border-[#E5E7EB] hover:bg-[#F9FAFB]"
                  )}
                  aria-label={tool.label}
                >
                  <Icon className="h-4 w-4 text-[#475569]" />
                </button>
              );
            })}
          </div>

          {/* Color Selection */}
          {selectedTool !== 'eraser' && (
            <>
              <div className="h-px bg-[#E5E7EB]" />
              <div className="flex justify-center gap-2">
                {penColors.map((color) => (
                  <button
                    key={color}
                    data-testid={`tid.tools.pen.color.${color.replace('#', '')}`}
                    onClick={() => handleColorSelect(color)}
                    className={cn(
                      "w-7 h-7 rounded-full border-2 transition-all",
                      "hover:scale-110",
                      selectedColor === color
                        ? "border-[#3478DD] shadow-sm"
                        : "border-transparent"
                    )}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export class PenTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'pen',
    name: 'Draw',
    icon: 'pen',
    category: 'create',
    description: 'Draw and annotate',
    version: '1.0.0',
    
    shortcuts: [
      {
        key: 'P',
        modifiers: [],
        description: 'Activate pen tool'
      }
    ],
    
    ui: {
      toolbar: true,
      contextMenu: false,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: false,
      statusBar: false
    },
    
    capabilities: {
      modifiesShapes: false,
      createsShapes: true,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: false,
      supportsBatchOperations: false
    }
  };

  private isDrawing = false;
  private currentPath: number[] = [];
  private strokeColor = '#000000';
  private strokeWidth = 2;
  private strokeOpacity = 1;
  private mode: PenMode = 'pen';
  private erasedNodeIds: Set<string> = new Set();
  private eraserRadius = 12; // tuned to feel natural for pointer sampling
  private isShiftPressed: boolean = false;
  private constraintDirection: 'horizontal' | 'vertical' | null = null;
  private startPoint: { x: number; y: number } | null = null;
  private readonly CONSTRAINT_THRESHOLD = 5; // pixels to avoid jitter
  private documentStartListenersAttached = false;
  private documentDrawListenersAttached = false;
  private processedDocumentPointerEvents = new WeakSet<Event>();

  private canMutate(): boolean {
    return useDiagramStore.getState().canWrite;
  }

  onActivate(context: IToolContext): void {
    super.onActivate(context);
    // Set up drawing event handlers
    if (context.stage) {
      context.stage.off('.pen');
      // change cursor to pen and mark canvas in drawing mode
      try {
        const container = context.stage.container();
        container.setAttribute('data-drawing-cursor', '1');
        container.style.cursor = 'crosshair';
      } catch {
        // Stage container can disappear during route teardown.
      }
      context.stage.on('mousedown.pen touchstart.pen', this.handleDrawStart.bind(this));
      context.stage.on('mousemove.pen touchmove.pen', this.handleDrawMove.bind(this));
      context.stage.on('mouseup.pen touchend.pen', this.handleDrawEnd.bind(this));
    }
    this.attachDocumentStartListeners();
    // Add keyboard handler for Esc key and shift key
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    // Use capture phase so Esc is caught even if a UI popover stops bubbling
    window.addEventListener('keydown', this.handleKeyDown, { capture: true } as AddEventListenerOptions);
    window.addEventListener('keyup', this.handleKeyUp, { capture: true } as AddEventListenerOptions);
  }

  onDeactivate(): void {
    this.detachDocumentStartListeners();
    this.detachDocumentDrawListeners();
    // Clean up event handlers
    if (this.context?.stage) {
      this.context.stage.off('.pen');
      try {
        const container = this.context.stage.container();
        container.removeAttribute('data-drawing-cursor');
        container.style.cursor = 'default';
      } catch {
        // Stage container can disappear during route teardown.
      }
    }
    window.removeEventListener('keydown', this.handleKeyDown, { capture: true } as AddEventListenerOptions);
    window.removeEventListener('keyup', this.handleKeyUp, { capture: true } as AddEventListenerOptions);
    // Reset shift state
    this.isShiftPressed = false;
    this.constraintDirection = null;
    this.startPoint = null;
    super.onDeactivate();
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      // End any live preview and clear drawing state
      if (this.isDrawing) {
        this.endPreview();
        this.resetDrawingState();
      }
      // Prevent default so the overlay doesn't immediately re-open
      e.preventDefault();
      // Deactivate pen tool and switch to selection via event
      if (this.context?.eventBus) {
        try {
          this.context.eventBus.emit('tool:activate', { id: 'genogram-selection' });
        } catch (err) {
          console.warn('Failed to deactivate pen tool:', err);
        }
      }
    } else if (e.key === 'Shift') {
      this.isShiftPressed = true;
      // If shift is pressed mid-drawing, we'll determine constraint direction on next mouse move
      // This ensures we have the current mouse position
    }
  }

  private handleKeyUp(e: KeyboardEvent): void {
    if (e.key === 'Shift') {
      this.isShiftPressed = false;
      // Clear constraint direction when shift is released
      if (this.isDrawing) {
        this.constraintDirection = null;
      }
    }
  }

  private resetDrawingState(): void {
    this.detachDocumentDrawListeners();
    this.isDrawing = false;
    this.currentPath = [];
    this.constraintDirection = null;
    this.startPoint = null;
  }

  private endPreview(): void {
    this.context?.eventBus.emit('pen:preview:end');
  }

  private getDiagramPoint(e: PenEvent): { x: number; y: number } | null {
    const stage = e.target.getStage();
    return this.getDiagramPointFromStage(stage);
  }

  private getDiagramPointFromStage(stage?: IToolContext['stage'] | null): { x: number; y: number } | null {
    const pos = stage?.getPointerPosition();
    if (!stage || !pos) {
      return null;
    }

    const scale = stage.scaleX();
    const origin = stage.position();
    return {
      x: (pos.x - origin.x) / scale,
      y: (pos.y - origin.y) / scale,
    };
  }

  private syncStagePointer(event: MouseEvent | TouchEvent): void {
    try {
      this.context?.stage?.setPointersPositions(event);
    } catch {
      // The stage may be unavailable while the route is tearing down.
    }
  }

  private cancelCanvasEvent(e: PenEvent): void {
    e.evt.preventDefault();
    e.evt.stopPropagation();
    e.evt.stopImmediatePropagation();
    e.cancelBubble = true;
  }

  private attachDocumentStartListeners(): void {
    if (this.documentStartListenersAttached) return;
    window.addEventListener('mousedown', this.handleDocumentDrawStart, true);
    window.addEventListener('touchstart', this.handleDocumentDrawStart, { capture: true, passive: false });
    this.documentStartListenersAttached = true;
  }

  private detachDocumentStartListeners(): void {
    if (!this.documentStartListenersAttached) return;
    window.removeEventListener('mousedown', this.handleDocumentDrawStart, true);
    window.removeEventListener('touchstart', this.handleDocumentDrawStart, true);
    this.documentStartListenersAttached = false;
  }

  private attachDocumentDrawListeners(): void {
    if (this.documentDrawListenersAttached) return;
    window.addEventListener('mousemove', this.handleDocumentDrawMove, true);
    window.addEventListener('mouseup', this.handleDocumentDrawEnd, true);
    window.addEventListener('touchmove', this.handleDocumentDrawMove, { capture: true, passive: false });
    window.addEventListener('touchend', this.handleDocumentDrawEnd, true);
    this.documentDrawListenersAttached = true;
  }

  private detachDocumentDrawListeners(): void {
    if (!this.documentDrawListenersAttached) return;
    window.removeEventListener('mousemove', this.handleDocumentDrawMove, true);
    window.removeEventListener('mouseup', this.handleDocumentDrawEnd, true);
    window.removeEventListener('touchmove', this.handleDocumentDrawMove, true);
    window.removeEventListener('touchend', this.handleDocumentDrawEnd, true);
    this.documentDrawListenersAttached = false;
  }

  private isPointerInsideStage(event: MouseEvent | TouchEvent): boolean {
    const stage = this.context?.stage;
    const container = stage?.container();
    if (!container) return false;
    const point = this.getClientPoint(event);
    if (!point) return false;
    const rect = container.getBoundingClientRect();
    if (
      point.clientX < rect.left ||
      point.clientX > rect.right ||
      point.clientY < rect.top ||
      point.clientY > rect.bottom
    ) {
      return false;
    }
    const hit = document.elementFromPoint(point.clientX, point.clientY);
    return Boolean(hit && container.contains(hit));
  }

  private getClientPoint(event: MouseEvent | TouchEvent): { clientX: number; clientY: number } | null {
    if ('clientX' in event) {
      return { clientX: event.clientX, clientY: event.clientY };
    }
    const touch = event.touches[0] ?? event.changedTouches[0];
    return touch ? { clientX: touch.clientX, clientY: touch.clientY } : null;
  }

  private beginDrawingAtPoint(point: { x: number; y: number }): void {
    this.isDrawing = true;
    this.currentPath = [point.x, point.y];
    // Store start point for constraint calculation
    this.startPoint = point;
    this.constraintDirection = null;
    this.attachDocumentDrawListeners();
    if (this.mode === 'eraser') {
      this.tryEraseAtPoint(point.x, point.y);
    }
    // Emit preview start
    this.context?.eventBus.emit('pen:preview:start', {
      points: this.currentPath.slice(),
      color: this.strokeColor,
      width: this.strokeWidth,
      opacity: this.strokeOpacity
    });
  }

  private handleDocumentDrawStart = (event: MouseEvent | TouchEvent): void => {
    if (this.isDrawing) return;
    if (!this.canMutate() || !this.isPointerInsideStage(event)) {
      this.resetDrawingState();
      return;
    }
    this.processedDocumentPointerEvents.add(event);
    this.syncStagePointer(event);
    const point = this.getDiagramPointFromStage(this.context?.stage);
    if (!point) {
      this.resetDrawingState();
      return;
    }
    if (event.cancelable) event.preventDefault();
    event.stopPropagation();
    this.beginDrawingAtPoint(point);
  };

  private handleDocumentDrawMove = (event: MouseEvent | TouchEvent): void => {
    if (!this.isDrawing) return;
    this.processedDocumentPointerEvents.add(event);
    this.syncStagePointer(event);
    if (event.cancelable) event.preventDefault();
    event.stopPropagation();
    const point = this.getDiagramPointFromStage(this.context?.stage);
    if (!point) return;
    this.appendDrawPoint(point);
  };

  private handleDocumentDrawEnd = (event: MouseEvent | TouchEvent): void => {
    if (!this.isDrawing) return;
    this.processedDocumentPointerEvents.add(event);
    this.syncStagePointer(event);
    if (event.cancelable) event.preventDefault();
    event.stopPropagation();
    this.handleDrawEnd();
  };

  private handleDrawStart(e: PenEvent): void {
    if (this.processedDocumentPointerEvents.has(e.evt)) return;
    if (!this.canMutate()) {
      this.resetDrawingState();
      return;
    }
    const point = this.getDiagramPoint(e);
    if (!point) {
      this.resetDrawingState();
      return;
    }
    this.beginDrawingAtPoint(point);
    // Prevent background drag-select - cancel both browser and Konva events
    this.cancelCanvasEvent(e);
  }

  private handleDrawMove(e: PenEvent): void {
    if (!this.isDrawing) return;
    if (this.processedDocumentPointerEvents.has(e.evt)) return;
    if (!this.canMutate()) {
      this.endPreview();
      this.resetDrawingState();
      return;
    }
    const point = this.getDiagramPoint(e);
    if (!point) {
      return;
    }
    this.appendDrawPoint(point);
    // Prevent selection box - cancel both browser and Konva events
    this.cancelCanvasEvent(e);
  }

  private appendDrawPoint(point: { x: number; y: number }): void {
    if (!this.isDrawing) return;
    if (!this.canMutate()) {
      this.endPreview();
      this.resetDrawingState();
      return;
    }
    let { x: cx, y: cy } = point;
    
    // Apply shift constraint (only for pen/highlighter modes, not eraser)
    if (this.mode !== 'eraser' && this.isShiftPressed && this.startPoint) {
      // Determine constraint direction if not already set, using current mouse position
      if (!this.constraintDirection) {
        this.determineConstraintDirection(cx, cy);
      }
      
      // Apply constraint based on direction
      if (this.constraintDirection === 'horizontal') {
        cy = this.startPoint.y; // Lock Y to start point
      } else if (this.constraintDirection === 'vertical') {
        cx = this.startPoint.x; // Lock X to start point
      }
    }
    
    this.currentPath.push(cx, cy);
    if (this.mode === 'eraser') {
      this.tryEraseAtPoint(cx, cy);
      this.context?.eventBus.emit('pen:preview:update', {
        points: this.currentPath.slice()
      });
      return;
    }
    this.context?.eventBus.emit('pen:preview:update', {
      points: this.currentPath.slice()
    });
  }

  private determineConstraintDirection(currentX: number, currentY: number): void {
    if (!this.startPoint) return;
    
    // Calculate movement from start point
    const deltaX = Math.abs(currentX - this.startPoint.x);
    const deltaY = Math.abs(currentY - this.startPoint.y);
    
    // Only determine direction if movement exceeds threshold (avoid jitter)
    const totalMovement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    if (totalMovement < this.CONSTRAINT_THRESHOLD) {
      return; // Wait for more movement
    }
    
    // Determine constraint direction based on dominant movement
    if (deltaX > deltaY) {
      this.constraintDirection = 'horizontal';
    } else {
      this.constraintDirection = 'vertical';
    }
  }

  private handleDrawEnd(): void {
    if (!this.isDrawing) return;
    this.detachDocumentDrawListeners();
    if (!this.canMutate()) {
      this.endPreview();
      this.resetDrawingState();
      return;
    }
    this.isDrawing = false;
    if (this.mode !== 'eraser' && this.currentPath.length >= 4) {
      const cmd = new CreateFreehandPathCommand({
        points: this.currentPath.slice(),
        color: this.strokeColor,
        width: this.strokeWidth,
        opacity: this.strokeOpacity,
        mode: this.mode
      });
      // Emit command for execution by command manager (handled in App.tsx)
      this.context?.eventBus.emit('command:execute', cmd);
    }
    // Clear preview
    this.endPreview();
    this.currentPath = [];
    // Reset constraint state for next stroke
    this.constraintDirection = null;
    this.startPoint = null;
    // Pen stays active for multiple strokes
  }

  execute(params?: PenExecuteParams): ICommand | null {
    if (params && params.command) {
      return params.command as ICommand;
    }
    if (params?.color) this.strokeColor = params.color;
    if (typeof params?.width === 'number') this.strokeWidth = params.width;
    if (typeof params?.opacity === 'number') this.strokeOpacity = params.opacity;
    if (params?.tool) {
      this.mode = params.tool;
      this.erasedNodeIds.clear();
      if (this.mode === 'eraser') {
        const eraserTool = penTools.find(t => t.id === 'eraser');
        if (eraserTool?.width) {
          this.eraserRadius = eraserTool.width / 2;
        }
      }
    }
    return null;
  }

  getToolbarComponent() {
    return PenToolComponent;
  }

  private tryEraseAtPoint(x: number, y: number) {
    if (!this.context) return;
    if (!this.canMutate()) return;
    const store = useDiagramStore.getState();
    const freehandNodes = store.nodes.filter((node: SceneNode) => node.type === 'freehand');
    const radius = this.eraserRadius * 1.5;
    const radiusSq = radius * radius;

    const eraseIds: string[] = [];
    for (const node of freehandNodes) {
      if (this.erasedNodeIds.has(node.id)) continue;
      const pts = Array.isArray(node.attrs?.points) ? (node.attrs.points as number[]) : [];
      if (pts.length < 4) continue;
      const offsetX = typeof node.x === 'number' ? node.x : 0;
      const offsetY = typeof node.y === 'number' ? node.y : 0;
      for (let i = 0; i < pts.length - 2; i += 2) {
        const x1 = pts[i] + offsetX;
        const y1 = pts[i + 1] + offsetY;
        const x2 = pts[i + 2] + offsetX;
        const y2 = pts[i + 3] + offsetY;
        if (this.pointToSegmentDistanceSq(x, y, x1, y1, x2, y2) <= radiusSq) {
          eraseIds.push(node.id);
          this.erasedNodeIds.add(node.id);
          break;
        }
      }
    }

    if (eraseIds.length > 0) {
      const cmd = new DeleteNodeCommand(eraseIds);
      this.context.eventBus.emit('command:execute', cmd);
      const { layer, stage } = this.context;
      requestAnimationFrame(() => {
        layer?.batchDraw();
        stage?.batchDraw();
      });
    }
  }

  private pointToSegmentDistanceSq(px: number, py: number, x1: number, y1: number, x2: number, y2: number) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    if (dx === 0 && dy === 0) {
      const ddx = px - x1;
      const ddy = py - y1;
      return ddx * ddx + ddy * ddy;
    }
    const t = ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy);
    const clamped = Math.max(0, Math.min(1, t));
    const cx = x1 + clamped * dx;
    const cy = y1 + clamped * dy;
    const ddx = px - cx;
    const ddy = py - cy;
    return ddx * ddx + ddy * ddy;
  }
}
